import PropTypes from 'prop-types';
import { Box, Button, Typography } from '@mui/material';
import { buildDraftOutreachSources } from 'features/IRWorkstation/utils/draftOutreachSources';
import { buildCrmRecordUrl } from 'features/IRWorkstation/utils/overview';

const SourcedRecordCard = ({ label, primaryText, secondaryText, salesforceUrl, onViewInCrmClick }) => (
	<Box
		sx={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			gap: 1,
			padding: '10px 12px',
			backgroundColor: 'background.paper',
			borderRadius: '8px',
			border: '0.5px solid',
			borderColor: 'border.light',
		}}
	>
		<Box sx={{ flex: 1, minWidth: 0 }}>
			<Typography
				sx={{
					fontSize: '10px',
					fontWeight: 600,
					textTransform: 'uppercase',
					letterSpacing: '0.5px',
					color: 'text.tertiary',
					mb: '4px',
				}}
			>
				{label}
			</Typography>
			<Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary' }}>
				{primaryText}
			</Typography>
			{secondaryText && (
				<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
					{secondaryText}
				</Typography>
			)}
		</Box>

		{salesforceUrl && (
			<Button
				component='a'
				href={salesforceUrl}
				target='_blank'
				rel='noopener noreferrer'
				onClick={onViewInCrmClick}
				variant='outlined'
				size='small'
				sx={{
					fontSize: '11px',
					fontWeight: 500,
					textTransform: 'none',
					whiteSpace: 'nowrap',
					backgroundColor: 'status.blue.bg',
					borderColor: 'status.blue.border',
					color: 'status.blue.text',
					'&:hover': {
						backgroundColor: 'status.blue.hover',
						borderColor: 'status.blue.border',
					},
				}}
			>
				View in CRM
			</Button>
		)}
	</Box>
);

SourcedRecordCard.propTypes = {
	label: PropTypes.string.isRequired,
	primaryText: PropTypes.string.isRequired,
	secondaryText: PropTypes.string,
	salesforceUrl: PropTypes.string,
	onViewInCrmClick: PropTypes.func,
};

/**
 * The Salesforce records that grounded a generated draft, so the user can see
 * the output came from their own org rather than being invented.
 */
const SourcedRecordsSection = ({ context, salesforceOrgUrl, onViewInCrmClick }) => {
	const records = buildDraftOutreachSources(context);

	if (records.length === 0) return null;

	return (
		<Box data-testid='sourced-records-section' sx={{ mt: 2 }}>
			<Typography
				sx={{
					fontSize: '11px',
					fontWeight: 600,
					textTransform: 'uppercase',
					letterSpacing: '0.5px',
					color: 'text.tertiary',
					mb: '8px',
				}}
			>
				Sourced Records
			</Typography>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
				{records.map((record, index) => (
					<SourcedRecordCard
						key={record.id || `${record.label}-${index}`}
						label={record.label}
						primaryText={record.primaryText}
						secondaryText={record.secondaryText}
						salesforceUrl={buildCrmRecordUrl(salesforceOrgUrl, record.id)}
						onViewInCrmClick={onViewInCrmClick}
					/>
				))}
			</Box>
		</Box>
	);
};

SourcedRecordsSection.propTypes = {
	context: PropTypes.object,
	salesforceOrgUrl: PropTypes.string,
	onViewInCrmClick: PropTypes.func,
};

export default SourcedRecordsSection;
